import { AnalyticsLogger, SourceType } from '@wix/answers-api';
import { pageView } from '@wix/bi-logger-new-help-center/v2';
import {
  firstAnonymous,
  pageView as pageViewPblc,
} from '@wix/bi-logger-pblc/v2';
import { useHttpClient } from '@wix/fe-essentials-standalone';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useCallback, useEffect } from 'react';

import { BI } from '../../constants';
import { useBI } from '../../hooks/useBI';
import { useHistoryContext } from '../../hooks/useRouteHistory';
import { CustomWindow, PageType } from '../../types';
import { mapArticleTypeToItemType } from '../../utils/bi';
import { hasWixClientId } from '../../utils/login';
import { PageData, PageDataArticle } from '../PageTemplate';

const NEW_SEARCH_RESULTS = 'new_search_results';

declare const window: CustomWindow;

type PageViewProps = {
  pageType: PageType;
  pageData: PageData;
  itemId?: string;
};

export const PageView: FunctionComponent<PageViewProps> = ({
  pageData,
  pageType,
  itemId,
}) => {
  const { sendBIEvent } = useBI();
  const router = useRouter();
  const { prePageUrl } = useHistoryContext();
  const { locale } = router;
  const httpClient = useHttpClient();

  const getArticleFeedbackVersion = (articleType?: number) => {
    if (mapArticleTypeToItemType(articleType) === BI.ITEM_TYPES.ARTICLE) {
      return BI.FEEDBACK_DETAILS.OLD;
    } else {
      return undefined;
    }
  };

  const articlePageViewItemVersion = (article: PageDataArticle) => {
    const version = [
      getArticleFeedbackVersion(article.type),
      'reading_time_true',
      NEW_SEARCH_RESULTS,
    ];

    const itemVersions = version.filter(Boolean);
    return itemVersions.length > 0 ? itemVersions.join(', ') : undefined;
  };

  const sendPageViewEvent = useCallback(async (): Promise<void> => {
    const emptyPageData = Object.keys(pageData).length === 0;
    const urlSearchParams = new URLSearchParams(location.search);
    const { innerWidth: screenWidth, innerHeight: screenHeight } = window;

    if (!hasWixClientId()) {
      await sendBIEvent(
        firstAnonymous({
          app_url: `${location.origin}${location.pathname}`,
          _artificial_ts: new Date().getTime(),
          // @ts-expect-error wrong type
          utm_campaign: urlSearchParams.get('utm_campaign'),
          // @ts-expect-error wrong type
          experiment_id: urlSearchParams.get('experiment_id'),
          referral: encodeURIComponent(document.referrer),
          _artifact_name: 'support',
        })
      );
    } else {
      await sendBIEvent(
        pageViewPblc({
          app_url: `${location.origin}${location.pathname}`,
          _artificial_ts: new Date().getTime(),
          // @ts-expect-error wrong type
          utm_campaign: urlSearchParams.get('utm_campaign'),
          // @ts-expect-error wrong type
          experiment_id: urlSearchParams.get('experiment_id'),
          referral: encodeURIComponent(document.referrer),
          _referer: location.href,
          _artifact_name: 'support',
        })
      );
    }

    const commonBiData = {
      kb_lang: locale as string,
      referral_url: prePageUrl,
      screen_width: screenWidth,
      screen_height: screenHeight,
    };

    switch (pageType) {
      case PageType.Article:
        if (itemId && pageData?.article) {
          await sendBIEvent(
            pageView({
              ...commonBiData,
              source_name: BI.SOURCE_NAMES.ARTICLE,
              item_id: itemId,
              item_type: mapArticleTypeToItemType(pageData.article.type),
              item_version: articlePageViewItemVersion(pageData.article),
              page_load_at_top: window?.scrollY < 300 ? true : false,
            })
          );

          // Special for Answers BI
          try {
            if (location.hostname === 'localhost') {
              return;
            }
            AnalyticsLogger.logPageView(
              (url: string, data: unknown) =>
                httpClient.post(`${location.origin}${url}`, data),
              '/api/v1',
              pageData.article.locale || '',
              SourceType.HELP_CENTER,
              null,
              pageData.article.id || ''
            );
          } catch (e) {
            console.error('Failed to send page view event to Answers BI', e);
          }
        }
        break;
      case PageType.SearchResults:
        if (
          pageData?.searchResults &&
          pageData?.searchResults.items?.length > 0
        ) {
          const term = new URLSearchParams(location.search).get('term') || '';
          await sendBIEvent(
            pageView({
              ...commonBiData,
              source_name: BI.SOURCE_NAMES.SEARCH_RESULTS,
              question: term as string,
              item_version: NEW_SEARCH_RESULTS,
              presented_content: JSON.stringify(
                pageData?.searchResults.items.map((article, index) => ({
                  order: index + 1,
                  item_id: article.id,
                  locale,
                  type: mapArticleTypeToItemType(article.type),
                }))
              ),
            })
          );
        }
        break;
      case PageType.Category:
        if (itemId && pageData.category) {
          await sendBIEvent(
            pageView({
              ...commonBiData,
              source_name: BI.SOURCE_NAMES.CATEGORY,
              item_id: itemId,
              referral_url: prePageUrl,
              item_version: NEW_SEARCH_RESULTS,
            })
          );
        }
        break;
      case PageType.KnownIssues:
        if (itemId && pageData.category) {
          await sendBIEvent(
            pageView({
              ...commonBiData,
              source_name: BI.SOURCE_NAMES.KNOWN_ISSUES,
              item_id: itemId,
              referral_name: router.query?.referral as string,
              item_version: NEW_SEARCH_RESULTS,
            })
          );
        }
        break;
      case PageType.Homepage:
        await sendBIEvent(
          pageView({
            ...commonBiData,
            source_name: BI.SOURCE_NAMES.HOME,
            referral_name: router.query?.utm_source as string,
            item_version: `new_home_page, ${NEW_SEARCH_RESULTS}`,
          })
        );
        break;
      case PageType.Ticket:
        if (itemId && emptyPageData) {
          await sendBIEvent(
            pageView({
              ...commonBiData,
              source_name: BI.SOURCE_NAMES.TICKET,
              item_id: itemId as string,
              item_type: BI.ITEM_TYPES.TICKET,
            })
          );
        }
        break;
      case PageType.Profile:
        if (itemId && emptyPageData) {
          await sendBIEvent(
            pageView({
              ...commonBiData,
              source_name: BI.SOURCE_NAMES.PROFILE,
              item_id: itemId as string,
              item_type: BI.ITEM_TYPES.PROFILE,
            })
          );
        }
        break;
      case PageType.Contact:
        await sendBIEvent(
          pageView({
            ...commonBiData,
            source_name: BI.SOURCE_NAMES.CONTACT,
            item_name: BI.ITEM_TYPES.CONTACT,
          })
        );
        break;
      default:
        break;
    }
  }, [locale, pageData, pageType, prePageUrl, itemId]);

  useEffect(() => {
    void (async () => {
      await sendPageViewEvent();
    })();
  }, [itemId]);
  return <></>;
};
